import { ref, watch, computed } from '@vue/composition-api';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import useUtils from '@/composables/useUtils';
import useTour from '@/modules/frontier-tour/composables/useTour';

export default function useTourList() {
  const toast = useToast();
  const { formatCurrency, convertUtcToLocalDate, formatDateDdMmYyyyHi, formatDateYyyyMmDd, convertDateToUTC } = useUtils();
  const { getTours } = useTour();

  const perPageOptions = [10, 25, 50, 100];
  const tableColumns = [
    { key: 'voucherNum', sortable: true },
    { key: 'tourType' },
    { key: 'tourStatusId', label: 'status' },
    {
      key: 'tourTotalFee',
      label: 'total',
      sortable: true,
      formatter: (val) => formatCurrency(val),
    },
    {
      key: 'dateAddedUtc',
      label: 'date added',
      sortable: true,
      sortable: true,
      formatter: (val) => `${formatDateDdMmYyyyHi(convertUtcToLocalDate(val))}`,
    },
    { key: 'actions' },
  ];

  const pageNumber = ref(1);
  const orderDesc = ref(true);
  const pageSize = ref(10);
  const refTourListTable = ref(null);
  const orderBy = ref('id');
  const totalTours = ref(0);

  const tourTypeFilter = ref(
    localStorage.getItem('tourList-tourTypeFilter') == 'null'
      ? null
      : JSON.parse(localStorage.getItem('tourList-tourTypeFilter'))
  );
  const tourStatusFilter = ref(
    localStorage.getItem('tourList-tourStatusFilter') == 'null'
      ? null
      : JSON.parse(localStorage.getItem('tourList-tourStatusFilter'))
  );
  const searchQuery = ref(
    localStorage.getItem('tourList-searchQuery') == ''
      ? ''
      : localStorage.getItem('tourList-searchQuery')
  );
  const searchByDate = ref(
    localStorage.getItem('tourList-searchByDate') == ''
      ? ''
      : localStorage.getItem('tourList-searchByDate')
  );

  const dataMeta = computed(() => {
    const localItemsCount = refTourListTable.value
      ? refTourListTable.value.localItems.length
      : 0;
    return {
      from: pageSize.value * (pageNumber.value - 1) + (localItemsCount ? 1 : 0),
      to: pageSize.value * (pageNumber.value - 1) + localItemsCount,
      of: totalTours.value,
    };
  });

  watch(
    [pageNumber, pageSize, tourTypeFilter, tourStatusFilter, searchByDate],
    () => {
      localStorage.setItem(
        'tourList-tourTypeFilter',
        JSON.stringify(tourTypeFilter.value)
      );
      localStorage.setItem(
        'tourList-tourStatusFilter',
        JSON.stringify(tourStatusFilter.value)
      );
      localStorage.setItem(
        'tourList-searchByDate',
        searchByDate.value
      );
      refTourListTable.value.refresh();
    }
  );

  const refreshTable = () => {
    localStorage.setItem(
        'tourList-searchQuery',
        searchQuery.value
      );
    refTourListTable.value.refresh();
  };

  const clearSearchByDate = () => {
    searchByDate.value = '';
  };

  const fetchTours = (ctx, callback) => {
    const params = {
      searchQuery: searchQuery.value,
      searchByDate: searchByDate.value === '' ? null : formatDateYyyyMmDd(convertDateToUTC(searchByDate.value)),
      pageSize: pageSize.value,
      pageNumber: pageNumber.value,
      orderBy: orderBy.value,
      orderDesc: orderDesc.value,
      tourStatusId: tourStatusFilter.value?.id ?? undefined,
      tourTypeId: tourTypeFilter.value?.id ?? undefined,
    };

    getTours(params)
      .then((response) => {
        const { tours, total } = response;
        callback(tours);
        totalTours.value = total;
      })
      .catch(() => {
        toast.error({
          component: ToastificationContent,
          props: {
            title: 'Error fetching tours list',
            icon: 'AlertTriangleIcon',
          },
        });
      });
  };

  return {
    fetchTours,
    tableColumns,
    pageSize,
    pageNumber,
    totalTours,
    dataMeta,
    perPageOptions,
    searchQuery,
    searchByDate,
    orderBy,
    orderDesc,
    refTourListTable,
    tourStatusFilter,
    tourTypeFilter,
    clearSearchByDate,
    refreshTable
  };
}
